import { useTranslation } from 'react-i18next'
import { ChevronDownIcon, LogoutIcon } from '@/components/atoms'
import { UserCircleIcon } from '@/components/atoms/icons'
import { useProfileMenu } from './profile-menu.hook'

export interface ProfileMenuProps {
  name: string
  onLogout: () => void
  isLoggingOut?: boolean
}

export function ProfileMenu({
  name,
  onLogout,
  isLoggingOut = false,
}: ProfileMenuProps) {
  const { t } = useTranslation()
  const { isOpen, toggleMenu, containerRef } = useProfileMenu()

  return (
    <div ref={containerRef} className="relative">
      <button
        type="button"
        onClick={toggleMenu}
        aria-haspopup="menu"
        aria-expanded={isOpen}
        aria-label={t('profileMenu.ariaLabel')}
        className="flex cursor-pointer items-center gap-1 rounded-lg focus:outline-none focus-visible:ring-2 focus-visible:ring-indigo-800"
        data-testid="profile-menu-button"
      >
        <span className="flex size-8 shrink-0 items-center justify-center rounded-lg bg-white">
          <UserCircleIcon className="text-indigo-base size-6" />
        </span>
        <span className="max-w-40 truncate text-sm leading-5 tracking-[0.01rem] text-neutral-900">
          {name}
        </span>
        <span
          aria-hidden
          className="size-1.5 shrink-0 rounded-full bg-teal-300"
        />
        <ChevronDownIcon
          className={`text-indigo-base size-4 shrink-0 transition-transform duration-200 ${isOpen ? 'rotate-180' : ''}`}
        />
      </button>

      {isOpen && (
        <div
          role="menu"
          className="absolute right-0 z-[var(--z-header)] mt-2 min-w-40 overflow-hidden rounded-md border border-neutral-100 bg-white shadow-md"
        >
          <button
            type="button"
            role="menuitem"
            onClick={onLogout}
            disabled={isLoggingOut}
            className="flex w-full cursor-pointer items-center justify-between gap-2 px-4 py-2 text-left text-base text-neutral-900 transition-colors hover:bg-neutral-50 disabled:cursor-wait"
            data-testid="logout-button"
          >
            {t('home.logout')}
            <LogoutIcon className="text-indigo-base size-5" />
          </button>
        </div>
      )}
    </div>
  )
}
